/**
 * Tools > Bulk PDF export.
 *
 * Two calls: one that counts what the filters select, and one that downloads the
 * archive. The count is what lets the page say "142 documents" beside a button
 * that is about to render 142 PDFs, instead of the operator pressing it blind and
 * waiting to find out.
 *
 * The download is a hand-rolled `fetch` rather than `api.get`, for the same
 * reason `downloadDocPdf` in `sales.ts` is: the body is a zip, not JSON. It also
 * reads three response headers on the way past, so the page can tell a complete
 * archive from a capped one.
 */
import { API_BASE, api } from "./api";

export const BULK_PDF_TYPES = ["invoice", "estimate", "proposal", "creditnote"] as const;
export type BulkPdfType = (typeof BULK_PDF_TYPES)[number];

/** The statuses each kind offers, mirroring `crm-sales/lib/bulkPdf.ts`. A value
 *  the server does not recognise for the chosen kind is ignored there rather than
 *  refused, so the two lists drifting narrows the filter instead of breaking it.
 *  Several may be picked at once; an empty selection means every status. */
export const BULK_PDF_STATUS: Record<BulkPdfType, readonly string[]> = {
  invoice: ["draft", "sent", "partially_paid", "paid", "overdue", "cancelled"],
  estimate: ["draft", "sent", "accepted", "declined", "expired"],
  proposal: ["draft", "sent", "accepted", "declined"],
  creditnote: ["draft", "issued", "applied"],
};

export interface BulkPdfFilters {
  type: BulkPdfType;
  from?: string;
  to?: string;
  /** Zero or more statuses of the chosen kind. Empty means every status - the
   *  same answer as "All", and what the dropdown reads when nothing is ticked. */
  statuses?: string[];
  clientTag?: string;
}

export interface BulkPdfCount {
  total: number;
  cap: number;
  capped: boolean;
}

/** What the archive turned out to hold, read off the response headers. */
export interface BulkPdfResult {
  rendered: number;
  matched: number;
  failed: number;
}

function qs(f: BulkPdfFilters): string {
  const p = new URLSearchParams({ type: f.type });
  if (f.from) p.set("from", f.from);
  if (f.to) p.set("to", f.to);
  // One parameter holding a comma-separated list rather than one parameter per
  // status: the server has read `status` since this page was a one-of-N, and a
  // link saved back then still means what it said.
  if (f.statuses?.length) p.set("status", f.statuses.join(","));
  if (f.clientTag) p.set("clientTag", f.clientTag);
  return p.toString();
}

export const countBulkPdf = (filters: BulkPdfFilters, signal?: AbortSignal) =>
  api.get<BulkPdfCount>(`/admin/crm/sales/bulk-pdf/count?${qs(filters)}`, signal);

/** A number off a header, or zero when the header is missing - it is a label, not
 *  a decision, and a missing one must not throw over a download that worked. */
function headerNum(res: Response, name: string): number {
  const raw = Number(res.headers.get(name));
  return Number.isFinite(raw) ? raw : 0;
}

/**
 * What the saved file is called.
 *
 * Built here rather than taken from the server's `Content-Disposition`, because
 * there is nothing left to take it from: the response is turned into a blob URL,
 * and a blob carries no filename. Mirrors `bulkPdfZipName` on the server so the
 * archive is named the same whichever end you look at it from.
 */
export function bulkPdfZipName(f: BulkPdfFilters): string {
  const parts: string[] = [f.type];
  if (f.from) parts.push(f.from);
  if (f.to) parts.push(f.to);
  return `${parts.join("_")}.zip`;
}

/** Thrown when the filters select nothing. Its own class because the page says
 *  something different for it - "no documents" is an answer, not a fault. */
export class BulkPdfEmptyError extends Error {
  constructor() {
    super("no_documents");
    this.name = "BulkPdfEmptyError";
  }
}

export async function downloadBulkPdf(filters: BulkPdfFilters): Promise<BulkPdfResult> {
  const res = await fetch(`${API_BASE}/admin/crm/sales/bulk-pdf?${qs(filters)}`, { credentials: "include" });
  if (res.status === 404) throw new BulkPdfEmptyError();
  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = bulkPdfZipName(filters);
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Freed on the next tick rather than immediately: Safari has not started the
  // download by the time click() returns, and revoking under it cancels it. The
  // same note `lib/csv.ts` carries.
  setTimeout(() => URL.revokeObjectURL(url), 0);

  return {
    rendered: headerNum(res, "X-Bulk-Pdf-Rendered"),
    matched: headerNum(res, "X-Bulk-Pdf-Matched"),
    failed: headerNum(res, "X-Bulk-Pdf-Failed"),
  };
}
